import React, { ChangeEvent, useMemo, useRef, useState } from 'react';

import { parse } from 'papaparse';

import DownloadIcon from '@mui/icons-material/Download';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogProps,
  DialogTitle,
  Stack,
  Tab,
  Tabs,
  TextField,
  Typography,
} from '@mui/material';

import { SUPPLIER_NAMES } from '~/consts/bedbank';

import bedbankService, {
  BulkPatchSupplierPropertyCommitRow,
  BulkPatchSupplierPropertyInput,
  BulkPatchSupplierPropertyPreviewRow,
  BulkPatchSupplierPropertyValueChange,
} from '~/services/BedbankService';

import { downloadCsvFile } from '~/utils/csvDownload';

import BulkSupplierPropertyUpdateCommitTable from './BulkSupplierPropertyUpdateCommitTable';
import BulkSupplierPropertyUpdatePreviewTable from './BulkSupplierPropertyUpdatePreviewTable';
import { isSelectableAction } from './BulkSupplierPropertyUpdateUtils';

const CSV_HEADERS = [
  'propertyId',
  'supplier',
  'supplierPropertyId',
  'travelgateId',
  'supplierEnabled',
  'metasearchEnabled',
] as const;
const CSV_TEMPLATE = `${CSV_HEADERS.join(',')}\n`;
const PREVIEW_EXPORT_HEADERS = [
  'Property ID',
  'Supplier',
  'Action',
  'Content Pull',
  'Supplier Property ID',
  'Travelgate ID',
  'Supplier Enabled',
  'Metasearch Enabled',
  'Error',
];
const COMMIT_EXPORT_HEADERS = [
  'Property ID',
  'Supplier',
  'Supplier Property ID',
  'Travelgate ID',
  'Supplier Enabled',
  'Metasearch Enabled',
  'Error',
];

type CsvHeader = (typeof CSV_HEADERS)[number];
type CsvRow = Partial<Record<CsvHeader, string>>;
type TabValue = 'input' | 'preview' | 'commit';

interface Props {
  open: boolean;
  onClose: () => void;
}

interface ParseResult {
  errors: Array<string>;
  rows: Array<BulkPatchSupplierPropertyInput>;
}

function normaliseHeader(header: string) {
  return header.trim();
}

function parseOptionalCsvValue(value?: string): string | undefined {
  const trimmed = value?.trim();
  return trimmed ? trimmed : undefined;
}

function parseNullableCsvValue(value?: string): string | null | undefined {
  const trimmed = value?.trim();
  if (!trimmed) {
    return undefined;
  }

  return trimmed.toLowerCase() === 'null' ? null : trimmed;
}

function parseBooleanCsvValue(value: string | undefined, rowNumber: number, field: string, errors: Array<string>) {
  const trimmed = value?.trim();
  if (!trimmed) {
    return undefined;
  }

  if (trimmed.toLowerCase() === 'true') {
    return true;
  }
  if (trimmed.toLowerCase() === 'false') {
    return false;
  }

  errors.push(`Row ${rowNumber}: ${field} must be true or false`);
  return undefined;
}

function isSupplierName(value: string): value is BulkPatchSupplierPropertyInput['supplier'] {
  return SUPPLIER_NAMES.includes(value as (typeof SUPPLIER_NAMES)[number]);
}

function parseCsv(csvText: string): ParseResult {
  const parsed = parse<CsvRow>(csvText.replace(/^\uFEFF/, ''), {
    header: true,
    skipEmptyLines: 'greedy',
    transformHeader: normaliseHeader,
    transform: (value) => value.trim(),
  });
  const fields = parsed.meta.fields ?? [];
  const errors = parsed.errors.map(
    (error) => `Row ${typeof error.row === 'number' ? error.row + 2 : 1}: ${error.message}`,
  );

  if (fields.join(',') !== CSV_HEADERS.join(',')) {
    return {
      errors: [`CSV header must be exactly: ${CSV_HEADERS.join(',')}`],
      rows: [],
    };
  }

  const rows: Array<BulkPatchSupplierPropertyInput> = [];
  const rowNumbersByKey = new Map<string, number>();
  parsed.data.forEach((row, index) => {
    const rowNumber = index + 2;
    const propertyId = row.propertyId?.trim();
    const supplier = row.supplier?.trim();
    const parsedSupplier = supplier && isSupplierName(supplier) ? supplier : undefined;
    const supplierPropertyId = parseOptionalCsvValue(row.supplierPropertyId);
    const travelgateId = parseNullableCsvValue(row.travelgateId);
    const supplierEnabled = parseBooleanCsvValue(row.supplierEnabled, rowNumber, 'supplierEnabled', errors);
    const metasearchEnabled = parseBooleanCsvValue(row.metasearchEnabled, rowNumber, 'metasearchEnabled', errors);

    if (!propertyId) {
      errors.push(`Row ${rowNumber}: propertyId is required`);
      return;
    }
    if (!supplier) {
      errors.push(`Row ${rowNumber}: supplier is required`);
      return;
    }
    if (!parsedSupplier) {
      errors.push(`Row ${rowNumber}: supplier "${supplier}" is not valid`);
      return;
    }
    const rowKey = getRowKey({ propertyId, supplier: parsedSupplier });
    const firstRowNumber = rowNumbersByKey.get(rowKey);
    if (firstRowNumber) {
      errors.push(`Row ${rowNumber}: duplicate propertyId and supplier; first used on row ${firstRowNumber}`);
      return;
    }
    if (
      !supplierPropertyId &&
      typeof travelgateId === 'undefined' &&
      typeof supplierEnabled === 'undefined' &&
      typeof metasearchEnabled === 'undefined'
    ) {
      errors.push(
        `Row ${rowNumber}: at least one of supplierPropertyId, travelgateId, supplierEnabled, or metasearchEnabled is required`,
      );
      return;
    }
    if (errors.length > 0 && errors[errors.length - 1].startsWith(`Row ${rowNumber}:`)) {
      return;
    }

    rowNumbersByKey.set(rowKey, rowNumber);
    rows.push({
      propertyId,
      supplier: parsedSupplier,
      ...(supplierPropertyId ? { supplierPropertyId } : {}),
      ...(typeof travelgateId !== 'undefined' ? { travelgateId } : {}),
      ...(typeof supplierEnabled !== 'undefined' ? { supplierEnabled } : {}),
      ...(typeof metasearchEnabled !== 'undefined' ? { metasearchEnabled } : {}),
    });
  });

  return { errors, rows };
}

function getRowKey(row: Pick<BulkPatchSupplierPropertyInput, 'propertyId' | 'supplier'>) {
  return `${row.propertyId}:${row.supplier}`;
}

function sortPreviewRows(rows: Array<BulkPatchSupplierPropertyPreviewRow>) {
  const actionOrder: Record<BulkPatchSupplierPropertyPreviewRow['action'], number> = {
    error: 0,
    create: 1,
    're-create': 2,
    update: 3,
    'no-change': 4,
  };

  return [...rows].sort((rowA, rowB) => {
    const actionDiff = actionOrder[rowA.action] - actionOrder[rowB.action];
    if (actionDiff !== 0) {
      return actionDiff;
    }

    const contentPullDiff = Number(Boolean(rowB.contentPullRequired)) - Number(Boolean(rowA.contentPullRequired));
    if (contentPullDiff !== 0) {
      return contentPullDiff;
    }

    const propertyIdDiff = rowA.propertyId.localeCompare(rowB.propertyId);
    if (propertyIdDiff !== 0) {
      return propertyIdDiff;
    }

    return getRowKey(rowA).localeCompare(getRowKey(rowB));
  });
}

function sortCommitRows(rows: Array<BulkPatchSupplierPropertyCommitRow>) {
  return [...rows].sort((rowA, rowB) => {
    const errorDiff = Number(!rowA.error) - Number(!rowB.error);
    if (errorDiff !== 0) {
      return errorDiff;
    }

    const propertyIdDiff = rowA.propertyId.localeCompare(rowB.propertyId);
    if (propertyIdDiff !== 0) {
      return propertyIdDiff;
    }

    return getRowKey(rowA).localeCompare(getRowKey(rowB));
  });
}

function formatExportValue(value: string | boolean | null | undefined) {
  if (typeof value === 'undefined') {
    return '';
  }

  return `${value ?? 'null'}`;
}

function formatPreviewChangeForExport(
  change: BulkPatchSupplierPropertyValueChange | undefined,
  action: BulkPatchSupplierPropertyPreviewRow['action'],
) {
  if (!change) {
    return '';
  }

  const oldValue = formatExportValue(change.oldValue);
  const newValue = formatExportValue(change.newValue);
  if (action === 'create') {
    return newValue;
  }

  return oldValue === newValue ? oldValue : `${oldValue} ⇒ ${newValue}`;
}

function buildPreviewExportRows(rows: Array<BulkPatchSupplierPropertyPreviewRow>): Array<Record<string, unknown>> {
  return rows.map((row) => ({
    'Property ID': row.propertyId,
    Supplier: row.supplier,
    Action: row.action,
    'Content Pull': typeof row.contentPullRequired === 'undefined' ? '' : `${row.contentPullRequired}`,
    'Supplier Property ID': formatPreviewChangeForExport(row.supplierPropertyId, row.action),
    'Travelgate ID': formatPreviewChangeForExport(row.travelgateId, row.action),
    'Supplier Enabled': formatPreviewChangeForExport(row.supplierEnabled, row.action),
    'Metasearch Enabled': formatPreviewChangeForExport(row.metasearchEnabled, row.action),
    Error: row.error ?? '',
  }));
}

function buildCommitExportRows(rows: Array<BulkPatchSupplierPropertyCommitRow>): Array<Record<string, unknown>> {
  return rows.map((row) => ({
    'Property ID': row.propertyId,
    Supplier: row.supplier,
    'Supplier Property ID': formatExportValue(row.supplierPropertyId),
    'Travelgate ID': formatExportValue(row.travelgateId),
    'Supplier Enabled': formatExportValue(row.supplierEnabled),
    'Metasearch Enabled': formatExportValue(row.metasearchEnabled),
    Error: row.error ?? '',
  }));
}

function getExportTimestamp() {
  return new Date()
    .toISOString()
    .replace(/[-:]/g, '')
    .replace(/\.\d{3}Z$/, 'Z');
}

export default function BulkSupplierPropertyUpdateDialog({ open, onClose }: Props) {
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [activeTab, setActiveTab] = useState<TabValue>('input');
  const [csvText, setCsvText] = useState(CSV_TEMPLATE);
  const [previewRows, setPreviewRows] = useState<Array<BulkPatchSupplierPropertyPreviewRow>>([]);
  const [commitRows, setCommitRows] = useState<Array<BulkPatchSupplierPropertyCommitRow>>([]);
  const [selectedRowKeys, setSelectedRowKeys] = useState<Set<string>>(new Set());
  const [isPreviewing, setIsPreviewing] = useState(false);
  const [isCommitting, setIsCommitting] = useState(false);
  const [apiError, setApiError] = useState<string | null>(null);

  const parsedRows = useMemo(() => parseCsv(csvText), [csvText]);
  const sortedPreviewRows = useMemo(() => sortPreviewRows(previewRows), [previewRows]);
  const sortedCommitRows = useMemo(() => sortCommitRows(commitRows), [commitRows]);
  const selectablePreviewRows = useMemo(
    () => previewRows.filter((row) => isSelectableAction(row.action)),
    [previewRows],
  );
  const selectedRows = useMemo(
    () => parsedRows.rows.filter((row) => selectedRowKeys.has(getRowKey(row))),
    [parsedRows.rows, selectedRowKeys],
  );
  const canPreview = parsedRows.rows.length > 0 && parsedRows.errors.length === 0 && !isPreviewing;
  const canCommit = selectedRows.length > 0 && !isCommitting;

  const reset = () => {
    setActiveTab('input');
    setCsvText(CSV_TEMPLATE);
    setPreviewRows([]);
    setCommitRows([]);
    setSelectedRowKeys(new Set());
    setApiError(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  const handleDialogClose: DialogProps['onClose'] = (_, reason) => {
    if (reason === 'backdropClick') {
      return;
    }

    handleClose();
  };

  const handleCsvFileChange = (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) {
      return;
    }

    const reader = new FileReader();
    reader.onload = () => {
      const text = typeof reader.result === 'string' ? reader.result : '';
      setCsvText(text);
      setPreviewRows([]);
      setCommitRows([]);
      setSelectedRowKeys(new Set());
      setApiError(null);
      setActiveTab('input');
    };
    reader.readAsText(file);
    event.currentTarget.value = '';
  };

  const handlePreview = async () => {
    setApiError(null);
    setPreviewRows([]);
    setCommitRows([]);
    setSelectedRowKeys(new Set());

    if (parsedRows.errors.length > 0) {
      setActiveTab('input');
      return;
    }

    setIsPreviewing(true);
    try {
      const response = await bedbankService.bulkPatchSupplierProperties(parsedRows.rows, 'preview');
      const rows = response.result.preview ?? [];
      setPreviewRows(rows);
      setSelectedRowKeys(new Set(rows.filter((row) => isSelectableAction(row.action)).map(getRowKey)));
      setActiveTab('preview');
    } catch (error) {
      setApiError(error instanceof Error ? error.message : 'Failed to preview bulk supplier property update');
    } finally {
      setIsPreviewing(false);
    }
  };

  const handleCommit = async () => {
    setApiError(null);
    setIsCommitting(true);
    try {
      const response = await bedbankService.bulkPatchSupplierProperties(selectedRows, 'commit');
      setCommitRows(response.result.commit ?? []);
      setActiveTab('commit');
    } catch (error) {
      setApiError(error instanceof Error ? error.message : 'Failed to commit bulk supplier property update');
    } finally {
      setIsCommitting(false);
    }
  };

  const toggleRow = (row: BulkPatchSupplierPropertyPreviewRow) => {
    if (!isSelectableAction(row.action)) {
      return;
    }

    setSelectedRowKeys((current) => {
      const next = new Set(current);
      const key = getRowKey(row);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });
  };

  const toggleAllSelectableRows = () => {
    setSelectedRowKeys((current) => {
      if (current.size === selectablePreviewRows.length) {
        return new Set();
      }

      return new Set(selectablePreviewRows.map(getRowKey));
    });
  };

  const handlePreviewExport = () => {
    const timestamp = getExportTimestamp();
    downloadCsvFile(
      buildPreviewExportRows(sortedPreviewRows),
      PREVIEW_EXPORT_HEADERS,
      `bedbank-supplier-property-bulk-update-preview-${timestamp}.csv`,
    );
  };

  const handleCommitExport = () => {
    const timestamp = getExportTimestamp();
    downloadCsvFile(
      buildCommitExportRows(sortedCommitRows),
      COMMIT_EXPORT_HEADERS,
      `bedbank-supplier-property-bulk-update-commit-${timestamp}.csv`,
    );
  };

  return (
    <Dialog fullWidth maxWidth={false} open={open} onClose={handleDialogClose} PaperProps={{ sx: { width: '1200pt' } }}>
      <DialogTitle>Bulk Update Bedbank Supplier Properties</DialogTitle>
      <DialogContent>
        <Tabs value={activeTab} onChange={(_, value) => setActiveTab(value)} sx={{ mb: 2 }}>
          <Tab label="Input" value="input" />
          <Tab label={`Preview${previewRows.length ? ` (${previewRows.length})` : ''}`} value="preview" />
          <Tab label={`Commit${commitRows.length ? ` (${commitRows.length})` : ''}`} value="commit" />
        </Tabs>

        {apiError && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {apiError}
          </Alert>
        )}

        {activeTab === 'input' && (
          <Stack spacing={2}>
            <Alert severity="info">CSV header must be exactly: {CSV_HEADERS.join(',')}</Alert>
            {parsedRows.errors.length > 0 && (
              <Alert severity="error">
                <Typography fontWeight={600}>CSV issues</Typography>
                <Box component="ul" sx={{ pl: 2, my: 0.5 }}>
                  {parsedRows.errors.map((error) => (
                    <li key={error}>{error}</li>
                  ))}
                </Box>
              </Alert>
            )}
            <TextField
              label="Supplier property update CSV"
              value={csvText}
              onChange={(event) => {
                setCsvText(event.target.value);
                setPreviewRows([]);
                setCommitRows([]);
                setSelectedRowKeys(new Set());
              }}
              multiline
              minRows={12}
              fullWidth
              inputProps={{ spellCheck: false }}
            />
          </Stack>
        )}

        {activeTab === 'preview' && (
          <BulkSupplierPropertyUpdatePreviewTable
            rows={sortedPreviewRows}
            selectedRowKeys={selectedRowKeys}
            onToggleRow={toggleRow}
            onToggleAllRows={toggleAllSelectableRows}
          />
        )}

        {activeTab === 'commit' && <BulkSupplierPropertyUpdateCommitTable rows={sortedCommitRows} />}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>Close</Button>
        {activeTab === 'input' && (
          <>
            <Button component="label" startIcon={<UploadFileIcon />} variant="outlined">
              Import CSV
              <input ref={fileInputRef} hidden accept=".csv,text/csv" type="file" onChange={handleCsvFileChange} />
            </Button>
            <Button disabled={!canPreview} variant="contained" onClick={handlePreview}>
              {isPreviewing ? 'Previewing...' : 'Preview'}
            </Button>
          </>
        )}
        {activeTab === 'preview' && (
          <>
            <Button
              disabled={sortedPreviewRows.length === 0}
              startIcon={<DownloadIcon />}
              variant="outlined"
              onClick={handlePreviewExport}
            >
              Export CSV
            </Button>
            <Button disabled={!canCommit} variant="contained" onClick={handleCommit}>
              {isCommitting
                ? 'Committing...'
                : `Commit ${selectedRows.length} row${selectedRows.length === 1 ? '' : 's'}`}
            </Button>
          </>
        )}
        {activeTab === 'commit' && (
          <Button
            disabled={commitRows.length === 0}
            startIcon={<DownloadIcon />}
            variant="outlined"
            onClick={handleCommitExport}
          >
            Export CSV
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}
